import { InputSelect } from '@lmc/ui-kit'
import { Form } from 'antd'
import useJobTitles from 'hooks/useUser/useJobTitles'
import { NAMESPACE } from 'locale'
import { reportsConstants } from 'locale/team'
import { type ComponentType, type ReactNode, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { FormFieldLayout } from '../../../generic'

// FormFieldLayout is a legacy JS component typed only via PropTypes; give it a
// narrow prop type so it can be used from this typed component.
const FieldLayout = FormFieldLayout as unknown as ComponentType<{
  label?: string
  required?: boolean
  note?: ReactNode
  children?: ReactNode
}>

type JobTitleFieldProps = {
  value?: string
  onChange: (value: string) => void
  disabled?: boolean
  testId?: string
}

const JobTitleField = ({
  value,
  onChange,
  disabled = false,
  testId = 'job-title'
}: JobTitleFieldProps) => {
  const { t } = useTranslation(NAMESPACE.TEAM)
  const { jobTitles } = useJobTitles()
  const [form] = Form.useForm()

  const options = jobTitles.map((title) => ({ label: title, value: title }))

  // InputSelect reads its value from the surrounding antd Form store, not the
  // `value` prop, so we wrap it in a local Form and sync `value` in — otherwise
  // an existing job title won't pre-select when editing.
  useEffect(() => {
    form.setFieldsValue({ jobTitle: value ? [value] : [] })
  }, [form, value])

  // tags mode emits an array; keep only the last entry (max 100 chars) and
  // collapse the field back to that single tag.
  const handleChange = (val: string[] | string) => {
    const arr = Array.isArray(val) ? val : [val]
    const next = (arr[arr.length - 1] ?? '').toString().slice(0, 100)
    form.setFields([{ name: 'jobTitle', value: next ? [next] : [] }])
    onChange(next)
  }

  // FormFieldLayout supplies the label-left column (shared with the other
  // fields) and renders the hint under the input via `note`. InputSelect itself
  // is label-less so it sits in the input column like the sibling fields.
  return (
    <FieldLayout label="Job title" note={t(reportsConstants.JOB_TITLE_HINT)}>
      <Form
        form={form}
        component={false}
        initialValues={{ jobTitle: value ? [value] : [] }}
      >
        <InputSelect
          testId={testId}
          type="jobTitle"
          layout="column"
          showSearch
          mode="tags"
          disabled={disabled}
          options={options}
          onChange={handleChange}
        />
      </Form>
    </FieldLayout>
  )
}

export default JobTitleField
